import React, { useState, useCallback } from "react";

import {
  Badge,
  Popover,
  PopoverHeader,
  PopoverBody,
  UncontrolledTooltip,
  Spinner,
} from "reactstrap";

import { mutate } from "swr";
import { useSelector } from "react-redux";
import { getURLWithParams } from "utils/context";

import { formatCurrency, limitCaracter } from "utils";
import Avatar from "@components/avatar";

import * as S from "./Products.styles";

import FireIcon from "assets/icone-fire.png";
import RiskIcon from "assets/icone-risk.png";
import { Trash2 } from "react-feather";
import { handleConfirmAlert } from "components/Structure/ConfirmAlert";
import { useIgnoreProduct } from "useCases";
import { MOVEMENT_TYPE } from "constants/enums";

const percente = value =>
  Number(value / 100).toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: 2,
  });

const PopoverItems = ({ title, titleCard, id, limit = 35 }) => {
  const [isShowPopover, setIsShowPopover] = useState("");
  const PopoverId = `Popover-${id}`;

  const OpenPopover = () =>
    setIsShowPopover(isShowPopover === PopoverId ? "" : PopoverId);

  return title?.length > 20 ? (
    <>
      <span
        onMouseEnter={() => OpenPopover()}
        onMouseLeave={() => setIsShowPopover("")}
        id={PopoverId}
      >
        {limitCaracter(limit, title)}
      </span>
      <Popover
        isOpen={Boolean(isShowPopover)}
        placement="top"
        toogle={OpenPopover}
        target={PopoverId}
      >
        <PopoverHeader>{titleCard}</PopoverHeader>
        <PopoverBody>{title}</PopoverBody>
      </Popover>
    </>
  ) : (
    title
  );
};

export const BtnRemove = ({ row, revalidate }) => {
  const { onIgnoreProduct, isLoading } = useIgnoreProduct();
  const { issue_date_start: start, issue_date_end: end } = useSelector(
    state => state.taxAnalises,
  );

  const onSend = useCallback(async () => {
    await onIgnoreProduct({
      xprod: row.xprod,
      cprod: row?.cprod,
      cnpj_empresa: row?.cnpj_empresa,
      ean: row?.cean,
      tipo_movimento: MOVEMENT_TYPE.SAIDA,
    }).then(() => {
      const key = getURLWithParams("/tax-note/diagnostico/totals", {
        issue_date_start: start,
        issue_date_end: end,
        tipo: "XML",
      });
      mutate(key);
      revalidate();
    });
  }, [row, onIgnoreProduct, revalidate, start, end]);

  const onConfirmDelete = useCallback(() => {
    handleConfirmAlert(
      {
        icon: "info",
        title: "Remover produto",
        text: "Deseja remover esse produto da listagem?",
        textBtn: "Confirmar",
      },
      onSend,
    );
  }, [onSend]);

  return (
    <>
      {isLoading ? (
        <Spinner className="text-danger" />
      ) : (
        <>
          <Avatar
            color="light-danger"
            icon={<Trash2 size={20} />}
            id={`trash-${row?.key}`}
            onClick={onConfirmDelete}
          />
          <UncontrolledTooltip placement="top" target={`trash-${row?.key}`}>
            Excluir
          </UncontrolledTooltip>
        </>
      )}
    </>
  );
};

export const getColumns = (revalidate, isUserMix) => [
  {
    name: "",
    width: isUserMix ? "100px" : "55px",
    isType: "queima",
    cell: row => (
      <>
        <div className="d-flex align-items-center gap-1">
          {isUserMix ? <BtnRemove row={row} revalidate={revalidate} /> : null}
          <S.IconeCustom src={FireIcon} />
        </div>
      </>
    ),
  },
  {
    name: "",
    width: isUserMix ? "100px" : "55px",
    isType: "risco",
    cell: row => (
      <>
        <div className="d-flex align-items-center gap-1">
          {isUserMix ? <BtnRemove row={row} revalidate={revalidate} /> : null}
          <S.IconeCustom src={RiskIcon} />
        </div>
      </>
    ),
  },
  {
    name: "EAN",
    omit: true,
    sortField: "cean",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "Cod.",
    omit: true,
    sortField: "cprod",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "NCM",
    omit: true,
    sortField: "ncm",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "CEST",
    omit: true,
    sortField: "cest",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "CFOP",
    omit: true,
    sortField: "cfop",
    isOmitMobile: true,
    isShowTag: true,
    type: "secondary",
  },
  {
    name: "Produto",
    typesDefaults: [],
    sortField: "xprod",
    isTitle: true,
    minWidth: "320px",
    position: 1,
    cell: row => {
      return (
        <div className="d-flex justify-content-left align-items-center">
          <div className="d-flex flex-column">
            <h5 className="user-name">
              {!row.xprod && (
                <Badge style={{ marginTop: "3px" }}>
                  SEM DESCRIÇÃO DO PRODUTO
                </Badge>
              )}
              {PopoverItems({
                titleCard: "Descrição",
                title: row.xprod,
                limit: 25,
                id: row?.key,
              })}
            </h5>
            <div
              className="user-name"
              style={{
                gridGap: 2,
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                marginBottom: "3px",
              }}
            >
              <Badge color="light-secondary">Cod. Interno: {row.cprod}</Badge>
              {row?.cean && (
                <Badge color="light-secondary">EAN: {row?.cean}</Badge>
              )}
              &nbsp;
              {row?.ncm && (
                <Badge color="light-secondary">NCM: {row?.ncm}</Badge>
              )}
              &nbsp;
              {row?.cest && (
                <Badge color="light-secondary">CEST: {row?.cest}</Badge>
              )}
              &nbsp;
              {row?.cfop && (
                <Badge color="light-secondary">CFOP: {row?.cfop}</Badge>
              )}
            </div>
          </div>
        </div>
      );
    },
  },

  {
    name: "QUEIMA DE CAIXA ICMS",
    typesDefaults: [],
    sortField: "queima_caixa_icms",
    isType: "queima",
    position: 1,
    cell: row => {
      return formatCurrency(row?.queima_caixa_icms);
    },
  },
  {
    name: "QUEIMA DE CAIXA PIS/COFINS",
    typesDefaults: [],
    sortField: "queima_caixa_pis_cofins",
    isType: "queima",
    position: 1,
    cell: row => {
      return formatCurrency(row?.queima_caixa_pis_cofins);
    },
  },
  {
    name: "Risco Assumido ICMS",
    typesDefaults: [],
    sortField: "risco_assumido_icms",
    isType: "risco",
    position: 1,
    cell: row => {
      return formatCurrency(row?.risco_assumido_icms);
    },
  },
  {
    name: "Risco Assumido PIS/COFINS",
    typesDefaults: [],
    sortField: "risco_assumido_pis_cofins",
    isType: "risco",
    position: 1,
    cell: row => {
      return formatCurrency(row?.risco_assumido_pis_cofins);
    },
  },

  {
    name: "Valor do Produto",
    typesDefaults: [],
    sortField: "vprod",
    position: 1,
    typeFormatValue: "monay",
    omit: true,
    cell: row => {
      return formatCurrency(row?.vprod);
    },
  },

  {
    name: "% Queima de Caixa ICMS por período",
    typesDefaults: [],
    isType: "queima",
    sortField: "porcentagem_queima_caixa_icms_no_periodo",
    typeFormatValue: "percent",
    position: 1,
    cell: row => {
      return `${percente(row?.porcentagem_queima_caixa_icms_no_periodo)}`;
    },
  },
  {
    name: "% Queima de Caixa PIS/COFINS por período",
    typesDefaults: [],
    isType: "queima",
    sortField: "porcentagem_queima_caixa_pis_cofins_no_periodo",
    typeFormatValue: "percent",
    position: 1,
    cell: row => {
      return `${percente(row?.porcentagem_queima_caixa_pis_cofins_no_periodo)}`;
    },
  },
  {
    name: "% Risco de Assumido ICMS por período",
    typesDefaults: [],
    isType: "risco",
    typeFormatValue: "percent",
    sortField: "porcentagem_risco_assumido_icms_no_periodo",
    position: 1,
    cell: row => {
      return `${percente(row?.porcentagem_risco_assumido_icms_no_periodo)}`;
    },
  },
  {
    name: "% Risco de Assumido PIS/COFINS por período",
    typesDefaults: [],
    isType: "risco",
    typeFormatValue: "percent",
    sortField: "porcentagem_risco_assumido_pis_cofins_no_periodo",
    position: 1,
    cell: row => {
      return `${percente(
        row?.porcentagem_risco_assumido_pis_cofins_no_periodo,
      )}`;
    },
  },
];
